import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

function useCountUp(target: number, duration: number, started: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    setCount(0);
    const startTime = performance.now();
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return count;
}

interface StatCardProps {
  rawValue: string;
  label: string;
  started: boolean;
}

function StatCard({ rawValue, label, started }: StatCardProps) {
  const match = rawValue.match(/^([^0-9]*)([0-9]+[.,]?[0-9]*)(.*)$/);
  const prefix = match ? match[1] : '';
  const numStr = match ? match[2] : '0';
  const suffix = match ? match[3] : '';
  const isDecimal = numStr.includes(',') || (numStr.includes('.') && numStr.split('.')[1]?.length <= 1);
  const numericTarget = parseFloat(numStr.replace(',', '.').replace('.', ''));
  const count = useCountUp(numericTarget, 1800, started);

  const formatCount = () => {
    if (isDecimal) {
      const decimalPart = numStr.includes(',') ? numStr.split(',')[1] : numStr.split('.')[1];
      const divisor = Math.pow(10, decimalPart?.length || 1);
      return (count / divisor).toFixed(decimalPart?.length || 1).replace('.', numStr.includes(',') ? ',' : '.');
    }
    if (count >= 1000) {
      return count.toLocaleString('de-DE');
    }
    return count.toString();
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center hover:border-green-500/40 transition-all">
      <div className="text-3xl sm:text-4xl font-black text-green-500 mb-2">
        {prefix}{formatCount()}{suffix}
      </div>
      <div className="text-sm text-white/60">{label}</div>
    </div>
  );
}

interface TooltipProps {
  text: string;
  children: React.ReactNode;
}

function Tooltip({ text, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<'top' | 'bottom'>('top');
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos(rect.top < 160 ? 'bottom' : 'top');
    }
    setVisible(true);
  };

  return (
    <div
      ref={ref}
      className="relative inline-flex items-center gap-1.5 cursor-default group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <span className="w-4 h-4 flex items-center justify-center rounded-full bg-white/10 text-white/40 text-[10px] font-bold leading-none select-none group-hover:bg-green-500/20 group-hover:text-green-400 transition-colors">
        i
      </span>
      {visible && (
        <div
          className={`absolute z-50 left-0 w-64 bg-gray-900 border border-white/15 rounded-lg px-4 py-3 shadow-2xl pointer-events-none
            ${pos === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
        >
          <div className={`absolute left-4 w-2 h-2 bg-gray-900 border-white/15 rotate-45
            ${pos === 'top' ? 'bottom-[-5px] border-b border-r' : 'top-[-5px] border-t border-l'}`}
          />
          <p className="text-xs text-white/75 leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
}

export default function ROINew() {
  const { language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animStarted, setAnimStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setAnimStarted(false);
    const timer = setTimeout(() => setAnimStarted(true), 100);
    return () => clearTimeout(timer);
  }, [language]);

  const content = {
    de: {
      label: 'ROI-Kalkulation',
      headline: 'Investition, die sich in Monaten amortisiert',
      subtext: 'Basierend auf durchschnittlichen Telco-Kennzahlen: 10.000 Tickets/Monat, 50 Support-Mitarbeiter, 3.500€ Durchschnittsgehalt.',
      stats: [
        { value: '−420.000€', label: 'Jährliche Einsparung' },
        { value: '4,2 Monate', label: 'Break-Even' },
        { value: '350%', label: 'ROI nach 12 Monaten' }
      ],
      tableHeaders: ['Metrik', 'Vorher', 'Mit TELIQone', 'Einsparung'],
      tableRows: [
        {
          metric: 'Ø Bearbeitungszeit/Ticket',
          before: '12 Min.',
          after: '6 Min.',
          saving: '−50%',
          tooltip: 'Die durchschnittliche Zeit, die ein Mitarbeiter benötigt, um ein Support-Ticket vollständig zu bearbeiten – von der Annahme bis zur Lösung. TELIQone automatisiert Klassifizierung und Datenabruf, was die Bearbeitungszeit halbiert.'
        },
        {
          metric: 'Manuelle Rechnungsprüfung',
          before: '8 Std./Tag',
          after: '1 Std./Tag',
          saving: '−87,5%',
          tooltip: 'Täglicher Zeitaufwand für die manuelle Prüfung und Abstimmung von Rechnungen. TELIQone erkennt Anomalien automatisch und markiert nur echte Ausreißer zur manuellen Kontrolle.'
        },
        {
          metric: 'FTE für repetitive Tasks',
          before: '15 FTE',
          after: '5 FTE',
          saving: '−10 FTE',
          tooltip: 'Vollzeitäquivalente (FTE), die ausschließlich für wiederkehrende, regelbasierte Aufgaben eingesetzt werden. Durch KI-Automatisierung können 10 FTE auf wertschöpfende Tätigkeiten umgeschichtet werden.'
        },
        {
          metric: 'Fehlerquote',
          before: '8%',
          after: '<1%',
          saving: '−87,5%',
          tooltip: 'Anteil fehlerhafter Dateneingaben, Fehlklassifizierungen oder falscher Abrechnungen am Gesamtvolumen. TELIQone validiert Daten in Echtzeit und reduziert menschliche Fehler auf unter 1%.'
        },
        {
          metric: 'CSAT-Score',
          before: '72%',
          after: '94%',
          saving: '+22 Pkt.',
          tooltip: 'Customer Satisfaction Score – gemessen durch Post-Interaction-Umfragen. Schnellere Lösungszeiten und präzisere Antworten durch TELIQone steigern die Kundenzufriedenheit signifikant.'
        }
      ]
    },
    en: {
      label: 'ROI Calculation',
      headline: 'Investment that pays off in months',
      subtext: 'Based on average telco metrics: 10,000 tickets/month, 50 support staff, €3,500 average salary.',
      stats: [
        { value: '−€420,000', label: 'Annual Savings' },
        { value: '4.2 months', label: 'Break-Even' },
        { value: '350%', label: 'ROI after 12 months' }
      ],
      tableHeaders: ['Metric', 'Before', 'With TELIQone', 'Savings'],
      tableRows: [
        {
          metric: 'Avg. processing time/ticket',
          before: '12 min',
          after: '6 min',
          saving: '−50%',
          tooltip: 'The average time an agent spends fully resolving a support ticket — from intake to closure. TELIQone automates classification and data retrieval, cutting handling time in half.'
        },
        {
          metric: 'Manual invoice checking',
          before: '8 hrs/day',
          after: '1 hr/day',
          saving: '−87.5%',
          tooltip: 'Daily hours spent manually reviewing and reconciling invoices. TELIQone detects anomalies automatically and flags only genuine outliers for human review.'
        },
        {
          metric: 'FTE for repetitive tasks',
          before: '15 FTE',
          after: '5 FTE',
          saving: '−10 FTE',
          tooltip: 'Full-time equivalents dedicated solely to recurring, rule-based tasks. AI automation frees up 10 FTEs to be redeployed on higher-value work.'
        },
        {
          metric: 'Error rate',
          before: '8%',
          after: '<1%',
          saving: '−87.5%',
          tooltip: 'Share of incorrect data entries, misclassifications, or billing errors out of total volume. TELIQone validates data in real time, reducing human error to below 1%.'
        },
        {
          metric: 'CSAT score',
          before: '72%',
          after: '94%',
          saving: '+22 pts',
          tooltip: 'Customer Satisfaction Score — measured via post-interaction surveys. Faster resolution times and more accurate answers through TELIQone drive a significant uplift in customer satisfaction.'
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section id="roi" className="relative py-20 sm:py-28 bg-black">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12 sm:mb-16 fade-in-section">
          <div className="inline-block px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full mb-4">
            <span className="text-green-400 text-xs font-semibold uppercase tracking-widest">{t.label}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
            {t.headline}
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-3xl mx-auto leading-relaxed">
            {t.subtext}
          </p>
        </div>

        <div ref={sectionRef} className="grid sm:grid-cols-3 gap-5 mb-12 fade-in-section">
          {t.stats.map((stat, i) => (
            <StatCard key={i} rawValue={stat.value} label={stat.label} started={animStarted} />
          ))}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden fade-in-section">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  {t.tableHeaders.map((header, i) => (
                    <th key={i} className="text-left px-4 sm:px-6 py-4 text-sm font-semibold text-white/80">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.tableRows.map((row, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-all">
                    <td className="px-4 sm:px-6 py-4 text-sm text-white font-medium">
                      <Tooltip text={row.tooltip}>
                        <span>{row.metric}</span>
                      </Tooltip>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-white/60">{row.before}</td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-green-500 font-semibold">{row.after}</td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-green-500 font-bold">{row.saving}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
